import GameObject from '../../../GameObject/GameObject';
import AnimationClip from '../AnimationClip';
import BaseCard from '../../../Prototype/Level/Card/BaseCard';
import Renderable from '../../../Component/Renderable';
import TransformComponent from '../../../Component/Transform';
import { Easing } from '@tweenjs/tween.js';
import { AwaitIterators, ConcatIterators, PlayAnimation } from '../AnimationHelper';
import TornadoCard from '../../../Prototype/Level/Card/TornadoCard';
import BaseCardHandler from '../../Card/Handler/BaseCardHandler';
import CardSystem, { getCardHandler } from '../../Card/CardSystem';
import SpriteComponent from '../../../Component/Render/Sprite';
import { MathHelper } from '../../../Utility/MathHelper';
import Scene from '../../../Scene/Scene';
import AnimationFrame from '../../../Component/AnimationFrame';
import { createPositionTween, createAlphaTween, getTweenProps } from '../AnimationTweenHelper';
import Point from '../../../Utility/Point';
import Connected from 'Prototype/Level/Card/Modifier/Connected';
import { ShuffleTargetData } from 'System/Card/Animations/ShuffleCardAnimation';
import { DragonbonesDefault } from './DragonbonesDefault';
import Armature from 'Prototype/Armature';
import { CardWiggle } from './CardWiggle';
import { DragonbonesFadeIn } from './DragonbonesFadeIn';
import { delay } from 'Utility/Task/TaskQueue';

interface ICardTornadoMeta {
    scene: Scene;
    sourceCard: TornadoCard;
    targets: ShuffleTargetData[];
    nonTargetCardsInPlay: BaseCard[];
    cardScore: number;
    system: CardSystem;
}

export class CardTornado extends AnimationClip<ICardTornadoMeta> {
    public *execute(animator: GameObject, meta: ICardTornadoMeta): IterableIterator<number> {
        const animatorTransform = animator.getComponent(TransformComponent);
        animator.addChild(meta.sourceCard);

        const inPlayCardTweens = [];
        for (let card of meta.nonTargetCardsInPlay) {
            inPlayCardTweens.push(this.cardWiggle(animator, card));
        }

        const targetCardTweens = [];
        for (let target of meta.targets) {
            animator.addChild(target.card);
            const connectedModifier = target.card.getModifier(Connected);

            if (connectedModifier) {
                animator.addChild(connectedModifier.rope);
            }

            // Add as child to animator to bring in front of overlay.
            targetCardTweens.push(this.tornadoSpin(animator, target.card, target.oldValue, target.newValue));
        }

        meta.scene.emit('UpdateScoreProgress');

        // Fade out source card.
        const sourceCardAlphaTween = createAlphaTween({
            ...getTweenProps(meta.sourceCard, this, { alpha: 0 }),
            duration: 200,
        });

        const tornadoArmature = GameObject.Instantiate(Armature, {
            x: animatorTransform.width / 2,
            y: animatorTransform.height / 2,
            dragonbonesName: 'special_card_tornado',
            armatureName: 'Armature',
            animationName: 'animation',
            alpha: 0,
            playOnStart: false,
            playTimes: 1,
        });
        animator.addChild(tornadoArmature);

        yield* AwaitIterators([
            PlayAnimation(
                new DragonbonesFadeIn(tornadoArmature, {
                    concat: false,
                })
            ),
            this.startTween(sourceCardAlphaTween),
            ConcatIterators([delay(250), AwaitIterators(inPlayCardTweens)]),
            ConcatIterators([delay(300), AwaitIterators(targetCardTweens)]),
        ]);

        meta.system.sortCardsInPlay(meta.system.store.getState().levelState.cardsState);
    }

    private tornadoSpin(
        animator: GameObject,
        targetCard: BaseCard,
        oldValue: number,
        newValue: number
    ): IterableIterator<number> {
        const cardSpinElapsedTimeTween = this.getTween({
            progress: 0,
        });

        cardSpinElapsedTimeTween
            .to({ progress: 1 }, 1500)
            .easing(Easing.Quadratic.Out)
            .onUpdate(o => {
                this.setCardFlipRotation(targetCard, o.progress, 13);

                // Swap textures at 80% -- looks fine.
                if (o.progress > 0.8) {
                    targetCard
                        .getChildByName(BaseCard.CARD_FRONT_NAME)
                        .getComponent(SpriteComponent).texture = `card${newValue + 1}`;
                }
            });

        return this.startTween(cardSpinElapsedTimeTween);
    }

    private cardWiggle(animator: GameObject, targetCard: BaseCard): IterableIterator<number> {
        animator.addChild(targetCard);
        return PlayAnimation(new CardWiggle(targetCard, { delay: 100, playTimes: 7 }, true));
    }

    /**
     * Set the flip rotation given a percentage of time.
     * @param card
     * @param progress
     * @param spins
     */
    private setCardFlipRotation(card: BaseCard, progress: number, spins: number) {
        const transform: TransformComponent = card.getComponent(TransformComponent);

        const spinDuration = 1 / spins;
        const halfSpinDuration = spinDuration / 2;

        transform.scale.x = MathHelper.pingPong(progress + halfSpinDuration, halfSpinDuration) / halfSpinDuration;

        const faceUp = Math.floor((progress - halfSpinDuration) / spinDuration) % 2 === 0;

        const handler: BaseCardHandler = getCardHandler(card);
        handler.setCardFaceUp(faceUp);
    }
}
